import groq from "groq";
import { Page } from "../../wrappers/Page";
import { usePreviewSubscription } from "../../helpers/sanity/sanity";
import { getClient } from "../../helpers/sanity/server";
import { SchemaType } from "../../sanity/types";
import type { GetStaticProps } from "next";
import React from "react";
import { NavType } from "../../types";
import { navQuery } from "../../sections/Nav";
import { StoriesTemplate } from "../../templates/StoriesTemplate";
import { ctaSectionGroq } from "../../sections/CtaSection.sanity";
import { footerQuery } from "../../sections/Footer";
import { FooterProps } from "../../finder/Footer";

const schemaType: SchemaType = "landing.stories";

/**
 * Sanity Query
 */

const pageQuery = groq`
  * {
    "page": *[_type == "${schemaType}"]{
      _id,
      title, 
      "items": *[_type == "story"] {
        title,
        "slug": slug.current,
        "image": mainImage,
        "hasVideo": video,
        "subject": expertise->title
      },
      "ctaSection": ctaSection{
        ${ctaSectionGroq}
      },
    }[0],
    ${footerQuery},
    ${navQuery},
  }[0]
`;

interface PageQueryType {
  page: {
    _id?: string;
    title?: string;
    items?: { title: string; slug: string }[];
  };
  nav: NavType;
  footer: FooterProps | {};
}

/**
 * Page
 */

interface Props {
  data: {
    post: PageQueryType;
  };
  preview: boolean;
}

const SingletonPage = ({ data, preview }: Props) => {
  const { data: post } = usePreviewSubscription(pageQuery, {
    params: { slug: data.post?.page?._id },
    initialData: data.post,
    enabled: preview && Boolean(data.post?.page?._id),
  });

  if (!post?.page) {
    return <Page notFound={true} nav={post.nav} footer={post.footer} />;
  }

  const { title, _id, items } = post.page;

  return (
    <Page
      title={title}
      notFound={!_id}
      nav={post.nav}
      preview={preview}
      footer={post.footer}
    >
      <StoriesTemplate page={post.page} />
    </Page>
  );
};

export default SingletonPage;

/**
 * Static props
 */

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
  const post: PageQueryType = await getClient(preview).fetch(pageQuery);

  if (!post) return { props: { notFound: true } };

  return {
    props: {
      preview,
      data: { post },
    },
    revalidate: 60,
  };
};
